/**
 * Masthead — the platform's publication-style header mark.
 *
 * Two presentations:
 *   <Masthead variant="page" />  — large, centered wordmark for landing-style pages (Login, AcceptInvite)
 *   <Masthead variant="bar" />   — compact horizontal bar for logged-in app chrome (admin, client)
 *
 * Wordmark anatomy:
 *   CRE           (kicker, small-caps red)
 *   Property KB   (serif display wordmark)
 *   Commercial Real Estate · Valuation Analysis  (small-caps dateline)
 *   ───── ─       (two-tone brand bar: 65% era-red, 35% charcoal)
 */

import { Link } from "react-router-dom";

interface MastheadProps {
  variant?: "page" | "bar";
  as?: "div" | "header";
  linkTo?: string;
  showDateline?: boolean;
  showBrandBar?: boolean;
}

export function Masthead({
  variant = "page",
  as = "header",
  linkTo,
  showDateline = true,
  showBrandBar = true,
}: MastheadProps) {
  const Wrapper = as;

  if (variant === "bar") {
    // Compact chrome for authenticated app shells
    return (
      <Wrapper className="mast-bar">
        {linkTo ? (
          <Link to={linkTo} className="mast-bar-wordmark">
            <MastheadWordmark compact />
          </Link>
        ) : (
          <div className="mast-bar-wordmark">
            <MastheadWordmark compact />
          </div>
        )}
      </Wrapper>
    );
  }

  // Page-style: large, centered/left-aligned, full brand treatment
  return (
    <Wrapper className="mast-page">
      <MastheadWordmark />
      {showDateline && (
        <div className="mast-dateline">
          Commercial Real Estate &middot; Valuation Analysis
        </div>
      )}
      {showBrandBar && (
        <div className="brand-bar" aria-hidden="true">
          <span /><span />
        </div>
      )}
    </Wrapper>
  );
}

function MastheadWordmark({ compact = false }: { compact?: boolean }) {
  return (
    <h1 className={`mast-wordmark ${compact ? "is-compact" : ""}`}>
      <span className="mast-kicker" aria-hidden="true">CRE</span>
      <span className="mast-wordmark-body">
        <span className="mast-wordmark-main">Property KB</span>
        {compact && (
          <span className="visually-hidden"> — Commercial Real Estate Property Knowledge Base</span>
        )}
      </span>
    </h1>
  );
}
